import { DimensionWidget } from "@components/shared/DimensionWidget/DimensionWidget";
import { OwDeleteEditActions } from "@components/shared/OwDeleteEditActions/OwDeleteEditActions";
import { OwFnInput } from "@components/shared/OwFnInput/OwFnInput";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import OwPill from "@components/shared/OwPill/OwPill";
import { PackQtyReverseViewer } from "@components/shared/PackQtyReverseViewer/PackQtyReverseViewer";
import { sortValuesByCatalogItemOptionOrder } from "@components/shared/OrderItemOptions/OrderItemOptions";
import { IAddBulkItems } from "@models/IAddBulkItems";
import ICatalog_Item from "@models/ICatalog_Item";
import IOrder from "@models/IOrder";
import ISupplierModel from "@models/ISupplierModel";
import IUserUI from "@models/IUserUI";
import OrderEntryService from "@services/OrderEntryService";
import UIService from "@services/UIService";
import { index } from "d3";
import { startCase } from "lodash-es";
import { ChangeEvent, useState } from "react";

type Props = {
    order: IOrder;
    index: number;
    supplierModel: ISupplierModel;
    userModel: IUserUI;
    itemDetail: IAddBulkItems;
    removeCartItem: (item: ICatalog_Item, index: number) => void;
};

export const CatalogViewerCartItem = (props: Props) => {
    const item = props.itemDetail.item;

    const [showQtyOnHand, setShowQtyOnHand] = useState(false);
    const [existingQtyOnHand, setExistingQtyOnHand] = useState(0);
    const [qty, setQty] = useState(0);

    const [depth, setDepth] = useState<number>(item.depth || 0);

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;

        const details = OrderEntryService.doInventoryValidation(
            item.Catalog_Item,
            Number(value),
            props.order.Order_Items,
            props.supplierModel,
            props.order,
            0
        );

        if (details.enableAdd) {
            setQty(Number(value) || 0);
        } else {
            if (details.qty - details.maxQty > 0) {
                setQty(details.maxQty);
            }
        }
    };

    return (
        <div className={"CatalogViewerCartItem cart-row p-2"}>
            <div style={{ minHeight: "3rem" }} className={"d-flex w-100 "} key={item.Catalog_Item.guid}>
                <div className={"me-4"}>
                    <OwDeleteEditActions
                        isEditMode={false}
                        onTrashClickFn={() => {
                            props.removeCartItem(item.Catalog_Item, props.index);
                        }}
                        hideEdit={true}
                    />
                </div>
                <div className={"w-15"}>
                    <div>
                        <OwFnInput
                            tabIndex={0}
                            name={"qty"}
                            fn={handleChange}
                            min={0}
                            value={item.qty}
                            disableKeyUp={true}
                            onWheelFn={UIService.stopScrollWheel}
                            enableDebounce={true}
                        />
                    </div>
                    <div>
                        {item.Catalog_Item.min_order_qty ? (
                            <div className={"text-danger text-small"}>
                                <OwLabel title={"min_order_qty"} postText={":"} />
                                {item.Catalog_Item.min_order_qty}
                            </div>
                        ) : null}
                    </div>
                    {item.Catalog_Item.has_pack_qty ? (
                        <div>
                            <PackQtyReverseViewer catalogItem={item.Catalog_Item} orderItem={item} />
                        </div>
                    ) : null}
                    <div>
                        {showQtyOnHand ? (
                            <div>
                                <div>
                                    <OwLabel title={"qoh"} noBottomMargin={true} className={"me-2"} />
                                    {item.Catalog_Item.qty_on_hand}
                                </div>
                                {existingQtyOnHand > 0 ? (
                                    <div>
                                        <OwLabel title={"have"} noBottomMargin={true} className={"me-2"} />
                                        {existingQtyOnHand}
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                    </div>
                </div>
                <div className={"w-30 text-center"}>
                    {item.Catalog_Item.sku}
                    {item.is_credit ? (
                        <div className="no-wrap">
                            <OwPill title={"credit"} className={"text-small"} />
                        </div>
                    ) : null}
                </div>
                <div>
                    {props.supplierModel.show_dimension_widget && props.userModel.isSupplierUser ? (
                        <DimensionWidget
                            type="depth"
                            fn={(value: number) => setDepth(value)}
                            catalogViewer={true}
                            compact={true}
                            value={depth}
                            units="imperial"
                            targetUnitId={item.Catalog_Item?.dimension_unit_id}
                        />
                    ) : null}
                </div>
            </div>
            <div>
                <div className={"d-none d-md-inline-block pre-wrap"}>
                    {item.Catalog_Item.description}

                    <div className={props.itemDetail.itemOptionValues?.length ? "mt-3" : ""}>
                        {sortValuesByCatalogItemOptionOrder(props.itemDetail.itemOptionValues || [], item.Catalog_Item.Options).map(
                            (option) => {
                                const optionItem = item.Catalog_Item.Options?.find((o) => o.item_option_id === option.item_option_id);
                                return (
                                    <div key={option.item_option_id}>
                                        <b>{optionItem?.Item_Option?.name}:</b> {startCase(option.value)}
                                    </div>
                                );
                            }
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
